import { Text } from '@/components/ui/text';

/**
 * Parses and renders highlighted text from Algolia search results.
 *
 * Security considerations:
 * - Input comes from Algolia's _highlightResult/_snippetResult (trusted source)
 * - Only processes <em> and <mark> tags
 * - Rendered within React's JSX (provides XSS protection)
 *
 * Alternative solutions considered:
 * 1. Algolia's highlight helper (instantsearch.js):
 *    - Pros: Official solution, handles all edge cases
 *    - Cons: Not cross-platform (web-only), requires instantsearch.js dependency
 *    - Usage: import { highlight } from 'instantsearch.js/es/helpers'
 *
 * 2. DOMPurify:
 *    - Pros: Lightweight, fast HTML sanitizer
 *    - Cons: Web-only, would need different solution for React Native
 *
 * 3. html-react-parser:
 *    - Pros: Full-featured HTML parser
 *    - Cons: Web-only, would need different solution for React Native
 *
 * Current implementation chosen because:
 * - Works cross-platform (web + React Native)
 * - Lightweight, no additional dependencies
 * - Sufficient for current use case (trusted Algolia input)
 *
 * TODO: Revisit with a more robust solution if:
 * - We need to support user-generated content
 * - We need to handle more complex HTML
 * - We have specific security requirements
 * - We find edge cases in the current implementation
 */
function parseHighlightTags(value: string) {
  const tokens: { text: string; highlighted: boolean }[] = [];

  // Regex explanation:
  // <(em|mark)>    - Opening tag, captures 'em' or 'mark' in group 1
  // ([\s\S]*?)     - Captures any characters (including newlines) in group 2, non-greedy
  // <\/\1>         - Closing tag, uses \1 backreference to match the opening tag
  // g              - Global flag to find all matches
  const highlightTagRegex = /<(em|mark)>([\s\S]*?)<\/\1>/g;

  let lastIndex = 0;

  for (const m of value.matchAll(highlightTagRegex)) {
    if (m.index! > lastIndex) {
      tokens.push({ text: value.slice(lastIndex, m.index), highlighted: false });
    }

    tokens.push({ text: m[2], highlighted: true });

    lastIndex = m.index! + m[0].length;
  }

  if (lastIndex < value.length) {
    tokens.push({ text: value.slice(lastIndex), highlighted: false });
  }

  return tokens;
}

/**
 * How a search match is marked. Text that highlights a search term without Algolia's markup uses
 * it too, so a term looks the same in a result and in the inquiry it opens.
 */
export const SEARCH_MATCH_CLASS = 'bg-mark-100 font-bold text-mark-900';

export function Highlight({ rawHTML }: { rawHTML: string }) {
  const parts = parseHighlightTags(rawHTML);

  return (
    <>
      {parts.map((part, i) =>
        part.highlighted ? (
          <Text key={i} className={SEARCH_MATCH_CLASS}>
            {part.text}
          </Text>
        ) : (
          part.text
        )
      )}
    </>
  );
}
